import { Trophy, Clock, Ticket } from "lucide-react";
import { Contest, ContestTheme } from "@/hooks/useContest";
import { isPast, isFuture, differenceInDays, formatDistanceToNow } from "date-fns";
import { ContestDescription } from "./ContestDescription";
import { normalizeHeroAspect, HERO_ASPECT_CSS } from "@/lib/heroAspect";
import { useEffect, useState } from "react";

interface ContestHeaderProps {
  contest: Contest;
  totalEntries?: number; // User's entries
  contestTotalEntries?: number | null; // Contest's total entries (global)
  showEnteredHeading?: boolean;
}

function UrgencyPill({ daysLeft, endsAt }: { daysLeft: number; endsAt: string }) {
  const [displayCount, setDisplayCount] = useState(0);

  useEffect(() => {
    if (daysLeft <= 0) {
      setDisplayCount(0);
      return;
    }

    const duration = 800; // ms
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing could be added here, but linear is requested
      const currentVal = Math.floor(progress * daysLeft);
      setDisplayCount(currentVal);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDisplayCount(daysLeft);
      }
    };

    requestAnimationFrame(animate);
  }, [daysLeft]);

  return (
    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border shadow-sm ${daysLeft < 3
      ? "bg-red-50 border-red-200 text-red-700"
      : "bg-orange-50 border-orange-200 text-orange-700"
      }`}>
      <Clock className={`w-4 h-4 animate-pulse ${daysLeft < 3 ? "text-red-500" : "text-orange-500"}`} />
      <span className="font-semibold text-sm">
        {displayCount} {displayCount === 1 ? "day" : "days"} left to enter
        <span className={`text-xs ml-1 font-normal ${daysLeft < 3 ? "text-red-500" : "text-orange-500"}`}>
          · Ends {new Date(endsAt).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric"
          })}
        </span>
      </span>
    </div>
  );
}

export function ContestHeader({ contest, totalEntries, contestTotalEntries, showEnteredHeading }: ContestHeaderProps) {
  const hasEnded = contest.ends_at && isPast(new Date(contest.ends_at));
  const hasStarted = !contest.starts_at || isPast(new Date(contest.starts_at));
  const startsIn = contest.starts_at && isFuture(new Date(contest.starts_at))
    ? formatDistanceToNow(new Date(contest.starts_at), { addSuffix: true })
    : null;

  // Calculate days left
  const daysLeft = contest.ends_at && !hasEnded
    ? Math.ceil(differenceInDays(new Date(contest.ends_at), new Date()) + 1)
    : null;

  const theme = contest.theme_json as ContestTheme | null;

  // Determine layout mode
  const hasHero = !!contest.hero_image_url;

  return (
    <div className="slide-up text-center mb-6">
      {/* Layout Option 1: Hero Image Present (New Unified Unit) */}
      {hasHero ? (
        <div className="relative mb-14 w-full">
          {(() => {
            const aspect = normalizeHeroAspect(contest.hero_image_aspect);
            const commonClasses = "rounded-xl overflow-hidden shadow-sm w-full"; // Removed max-w-md to be full-width of container

            if (aspect === 'original') {
              return (
                <div className={commonClasses}>
                  <img
                    src={contest.hero_image_url}
                    alt={contest.name}
                    className="block w-full h-auto object-contain"
                  />
                </div>
              );
            }

            return (
              <div
                className={`${commonClasses} relative`}
                style={{ aspectRatio: HERO_ASPECT_CSS[aspect] }}
              >
                <img
                  src={contest.hero_image_url}
                  alt={contest.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            );
          })()}

          {/* Overlaid Logo (only if hero exists) */}
          {contest.logo_url && (
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 z-10">
              <div className="w-20 h-20 rounded-2xl bg-white shadow-lg ring-2 ring-white overflow-hidden p-1 flex items-center justify-center">
                <img
                  src={contest.logo_url}
                  alt={`${contest.name} logo`}
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Layout Option 2: No Hero (Legacy Stacked) */
        contest.logo_url && (
          <div className="mb-4">
            <img
              src={contest.logo_url}
              alt={`${contest.name} logo`}
              className="h-16 w-auto mx-auto object-contain"
            />
          </div>
        )
      )}

      {/* Contest name */}
      <h1
        className={`font-heading font-bold text-center ${hasHero ? "text-2xl mt-2" : "text-3xl md:text-4xl mb-3"
          }`}
        style={theme?.headingColor ? { color: theme.headingColor } : undefined}
      >
        {contest.name}
      </h1>

      {/* Urgency Pill (Deadline) */}
      {!hasEnded && hasStarted && daysLeft !== null && contest.ends_at && (
        <div className="flex justify-center mb-6 mt-3">
          <UrgencyPill
            daysLeft={daysLeft}
            endsAt={contest.ends_at}
          />
        </div>
      )}

      {/* Removed old separate Hero Image block as it's now moved up */}

      {/* Description */}
      {(contest.description_html || contest.description) && (
        <div className="text-muted-foreground max-w-lg mx-auto mb-4">
          <ContestDescription value={contest.description_html || contest.description} />
        </div>
      )}

      {/* Status badges */}
      <div className="flex flex-wrap items-center justify-center gap-3 mb-5">
        {hasEnded ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-destructive/10 text-destructive rounded-full text-sm font-medium">
            <Clock className="w-4 h-4" />
            Contest Ended
          </span>
        ) : (
          <>
            {startsIn && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent/10 text-accent rounded-full text-sm font-medium">
                <Clock className="w-4 h-4" />
                Starts {startsIn}
              </span>
            )}
            {daysLeft !== null && hasStarted && (
              // Removed old pill
              null
            )}
          </>
        )}

        {/* Global Total Entries Count */}
        {contestTotalEntries !== undefined && contestTotalEntries !== null && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-muted rounded-full text-sm font-medium border border-border">
            <Ticket className="w-4 h-4" />
            Total entries: {contestTotalEntries ?? 0}
          </span>
        )}
      </div>

      {/* End Date Text - REMOVED (Replaced by Urgency Pill) */}

      {totalEntries !== undefined && totalEntries > 0 && (
        <div className="flex flex-col items-center gap-2">
          {showEnteredHeading && (
            <h2 id="entered-to-win" className="font-heading font-bold text-xl text-foreground scroll-mt-6">
              You're Entered to Win
            </h2>
          )}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-success/10 text-success rounded-full font-semibold">
            <Trophy className="w-4 h-4" />
            {totalEntries} {totalEntries === 1 ? "Entry" : "Entries"} Earned
          </div>
        </div>
      )}
    </div>
  );
}
